<script lang="ts" setup>
import { toRef, computed } from 'vue';
import { EnumerationEvaluationOptionDto, EnumerationEvaluationParameterDto } from '@/api/generator';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { useField, useFieldArray } from 'vee-validate';
import EvaluationsManualTasksEnumTileParamEvaluationStateTile from './EvaluationsManualTasksEnumTileParamEvaluationStateTile.vue';
import { mdiDragHorizontal } from '@mdi/js';
import { Sortable } from 'sortablejs-vue3';
import { SortableEvent, SortableOptions } from 'sortablejs';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { useValidateFieldErrorMessage } from '@/components/forms/useValidateFieldErrorMessage';

interface EvaluationsManualTasksEnumTileProps {
  name: string; // evaluationscopes[0].evaluationparameters[{index}]
}

const props = defineProps<EvaluationsManualTasksEnumTileProps>();

const mode = useFormMode();

const emits = defineEmits<{
  remove: [param: EnumerationEvaluationParameterDto];
}>();

const { value: tileData } = useField<EnumerationEvaluationParameterDto>(toRef(() => props.name));

const { fields: stateArray, move } = useFieldArray<EnumerationEvaluationOptionDto>(
  toRef(() => `${props.name}.options`),
);

const { validate: validateOptions, errorMessage: optionsErrorMessage } =
  useValidateFieldErrorMessage(toRef(() => `${props.name}.options`));

function handleRemove() {
  emits('remove', tileData.value);
}

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    move(e.oldIndex, e.newIndex);
  }
}

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.handle',
  disabled: mode.value === 'details',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));
</script>

<template>
  <div class="tile elevation-2">
    <div v-if="mode !== 'details'" class="stripe left-stripe">
      <v-icon :icon="mdiDragHorizontal" class="ml-2 mt-3 handle cursor-move" />
    </div>

    <div class="w-100 pa-5">
      <v-row>
        <v-col cols="3">
          <EnumOrQuantityLabel :data="tileData" :label="$t('Enumeration_one')" />
        </v-col>

        <v-col cols="3">
          <VeeTextarea :label="$t('Instruction_one')" :name="`${name}.instruction`" :mode="mode" />
        </v-col>

        <v-col cols="6">
          <p class="v-label v-label-overwrite mb-1 d-block text-wrap">
            {{ $t('EnumerationItem_other') }}
          </p>

          <v-alert
            v-if="optionsErrorMessage"
            type="error"
            variant="tonal"
            class="text-body-1 text-center mb-2"
          >
            <template #text>
              {{ optionsErrorMessage }}
            </template>
          </v-alert>

          <Sortable
            class="w-100"
            :list="stateArray"
            :options="sortableOptions"
            itemKey="key"
            @end="sortingEndHandler"
          >
            <template #item="{ index, key }">
              <EvaluationsManualTasksEnumTileParamEvaluationStateTile
                :key="key"
                :name="`${props.name}.options[${index}]`"
                @enabled="validateOptions()"
              />
            </template>
          </Sortable>
        </v-col>
      </v-row>
    </div>
    <div v-if="mode !== 'details'" class="stripe right-stripe">
      <PtwDeleteButton @delete="handleRemove" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tile {
  border: 0 solid rgb(var(--v-theme-primary-lighten-4));
  border-radius: 8px;
  background: white;
  position: relative;
  display: flex;
}
.stripe {
  width: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  i {
    margin: 0 !important;
  }
}

.left-stripe {
  border-right: 1px solid rgb(var(--v-theme-primary-lighten-4));
}

.right-stripe {
  border-left: 1px solid rgb(var(--v-theme-primary-lighten-4));
}
</style>
